import _extends from "@babel/runtime/helpers/extends";
import React from 'react';
import Imgix from 'react-imgix';
import styled from 'styled-components';
import lazysizes from 'lazysizes';
import 'lazysizes/plugins/attrchange/ls.attrchange';
import 'lazysizes/plugins/blur-up/ls.blur-up';
import ImageContainer, { imageContainerKeys, imageContainerMixin } from './ImageContainer';
import { getMs, getPx } from '../helpers/common';
import { applyForDpr, getPreviewUrl, getQuality } from './helpers';
import { useDeviceType } from '../hooks';
import { IMAGE_BLUR_MODE, IMAGE_BLUR_RADIUS, IMAGE_NORMAL_TRANSITION_TIME, IMAGE_QUICK_TRANSITION_TIME, IMAGE_TRANSITION_EASING } from './constants';
import { withOmitProps } from '../helpers/elements';
import { EMPTY_PROPS } from '../helpers/types';
import { isDefined } from '../helpers/defined';
lazysizes.cfg.blurupMode = IMAGE_BLUR_MODE;
export const setLazySizesConfig = config => {
  for (const key in config) {
    lazysizes.cfg[key] = config[key];
  }
};

/*
// TODO: think about whether we even need this
if (process?.env?.LAZYSIZES_CONFIG) {
  const config =
    typeof process.env.LAZYSIZES_CONFIG === 'string'
      ? JSON.parse(process.env.LAZYSIZES_CONFIG)
      : process.env.LAZYSIZES_CONFIG
  setLazySizesConfig(config)
}
*/

const StyledImgix = styled(Imgix).withConfig(withOmitProps([...imageContainerKeys]))`
  object-fit: ${({
  $fit = 'cover'
}) => $fit};
  ${imageContainerMixin}
`;
const StyledImageContainer = styled(ImageContainer)`
  .blur-up {
    -webkit-filter: blur(${getPx(IMAGE_BLUR_RADIUS)});
    filter: blur(${getPx(IMAGE_BLUR_RADIUS)});
    transition: ${({
  $fast,
  $immediate
}) => $immediate ? 'none' : $fast ? `filter ${getMs(IMAGE_QUICK_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING},
          -webkit-filter ${getMs(IMAGE_QUICK_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING}` : `filter ${getMs(IMAGE_NORMAL_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING},
          -webkit-filter ${getMs(IMAGE_NORMAL_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING}`};
  }
  .blur-up.lazyloaded {
    -webkit-filter: unset;
    filter: unset;
  }

  .blur-up.ls-is-cached {
    transition: ${({
  $immediate
}) => $immediate ? 'none' : `filter ${getMs(IMAGE_QUICK_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING},
          -webkit-filter ${getMs(IMAGE_QUICK_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING} !important`};
  }
  .blur-up.ls-is-cached ~ .ls-blur-up-img {
    transition: ${({
  $immediate
}) => $immediate ? 'none' : `filter ${getMs(IMAGE_QUICK_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING},
          -webkit-filter ${getMs(IMAGE_QUICK_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING} !important`};
  }

  .ls-blur-up-img {
    ${imageContainerMixin}

    object-fit: ${({
  $fit = 'cover'
}) => $fit};
    will-change: opacity, filter, -webkit-filter;

    /* only if you want to change the blur-up option from always to auto or want to use blur up effect without a lowsrc image. */
    font-family: 'blur-up: ${({
  $blurMode
}) => $blurMode}', 'object-fit: ${({
  $fit
}) => $fit}';

    opacity: 1;
    -webkit-filter: blur(${getPx(IMAGE_BLUR_RADIUS)});
    filter: blur(${getPx(IMAGE_BLUR_RADIUS)});
    transition: ${({
  $fast,
  $immediate
}) => $immediate ? 'none' : $fast ? `filter ${getMs(IMAGE_QUICK_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING},
          -webkit-filter ${getMs(IMAGE_QUICK_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING},
          opacity ${getMs(IMAGE_QUICK_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING}` : `filter ${getMs(IMAGE_NORMAL_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING},
          -webkit-filter ${getMs(IMAGE_NORMAL_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING},
          opacity ${getMs(IMAGE_NORMAL_TRANSITION_TIME)} ${IMAGE_TRANSITION_EASING}`};
  }

  .ls-blur-up-img.ls-inview.ls-original-loaded {
    opacity: 0;
    -webkit-filter: unset;
    filter: unset;
  }
`;
const Image = /*#__PURE__*/React.forwardRef(({
  absolute = true,
  lazy = true,
  preview = true,
  backgroundColor,
  fit = 'cover',
  fast = false,
  immediate = false,
  imgixParams = EMPTY_PROPS,
  className,
  width,
  height,
  htmlAttributes = EMPTY_PROPS,
  alt = '',
  quality: qualityProp,
  ratio: ratioProp,
  ...props
}, forwardedRef) => {
  const {
    pixelRatio
  } = useDeviceType();
  const dpr = Math.min(Math.max(Math.ceil(pixelRatio), 1), 5);
  const quality = qualityProp ? applyForDpr(qualityProp, dpr) : null;
  const ratio = ratioProp ? applyForDpr(ratioProp, dpr) : null;
  const disableSrcSet = width && height && (isDefined(quality) || isDefined(ratio));
  return /*#__PURE__*/React.createElement(StyledImageContainer, {
    ref: forwardedRef,
    className: className,
    $absolute: absolute,
    $backgroundColor: backgroundColor,
    width: width,
    height: height,
    $immediate: immediate,
    $fast: fast,
    $fit: fit,
    $blurMode: IMAGE_BLUR_MODE
  }, /*#__PURE__*/React.createElement(StyledImgix, _extends({
    $fit: fit,
    $absolute: absolute,
    className: lazy ? `lazyload ${preview ? 'blur-up' : ''}` : '',
    attributeConfig: lazy ? {
      src: 'data-src',
      srcSet: 'data-srcset',
      sizes: 'data-sizes'
    } : {},
    htmlAttributes: {
      alt,
      ...(lazy && preview ? {
        src: getPreviewUrl(props),
        // low quality image url
        'data-lowsrc': getPreviewUrl(props) // low quality image url
      } : {
        src: 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==' // transparent gif, according to: https://github.com/aFarkas/lazysizes/blob/master/plugins/blur-up/ls.blur-up.js#L24
      }),

      ...htmlAttributes
    },
    imgixParams: {
      fit: fit === 'cover' ? 'crop' : 'clip',
      ...(disableSrcSet ? {
        q: getQuality(quality, imgixParams?.q, dpr)
      } : {}),
      ...imgixParams
    },
    disableQualityByDPR: disableSrcSet,
    disableSrcSet: disableSrcSet,
    imageWidth: width,
    imageHeight: height,
    width: disableSrcSet && typeof width === 'number' ? width * (ratio ?? dpr) : width,
    height: disableSrcSet && typeof height === 'number' ? height * (ratio ?? dpr) : height
  }, props)));
});
export default Image;